import React, { useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { getKvizPitanjaSearch, exportKvizPitanjaExcel } from '../../../server/server';
import '../../../style/UserManagement.css';
import { ReactComponent as SearchMagnifyIcon } from '../../../assets/svg/SearchMagnifyIcon.svg';

import Loading from '../../../utils/Loading';
import { TezinaPitanja } from '../../../model/enum/tezina-pitanja.enum';
import AddKvizPitanjeModal from '../admin-kategorije/AddKvizPitanjeModal';
import { deleteKvizPitanjeById, getOblasti, updateListeKvizPitanja } from '../../../server/server-kategorije-tree';
import { notif } from '../../utils/util';
import { Autocomplete, Chip, TextField } from '@mui/material';
import { kvizPitanjeTagsLista } from '../../../constatnts/ConstantsQuiz';
import MDEditor from '@uiw/react-md-editor';
import { mathPlugins } from '../../../utils/markdownMath';
import { importKvizPitanjaExcel, previewImportKvizPitanjaExcel } from '../../../server/server-kviz';

const limit = 20;
const tezinaOptions = ['LAKO', 'SREDNJE', 'TESKO', 'NAPREDNO'];

type FlagField = 'isLekcijaPitanje' | 'isKvizPitanje' | 'visible' | 'zaPraksu' | 'zaTakmicenje';
const flagColumns: { field: FlagField; label: string; title: string }[] = [
    { field: 'isLekcijaPitanje', label: 'Lekcija', title: 'Lekcija pitanje' },
    { field: 'isKvizPitanje', label: 'Kviz', title: 'Kviz pitanje' },
    { field: 'visible', label: 'Vidljivo', title: 'Vidljivo' },
    { field: 'zaPraksu', label: 'Vežbanje', title: 'Kviz Lovac - vežbanje' },
    { field: 'zaTakmicenje', label: 'Takm.', title: 'Kviz Lovac - takmičenje' },
];

// padajući filter: Svi / Da / Ne (prazno = svi)
function YesNoFilter({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
    return (
        <label className="flex items-center gap-1">
            <span className="text-primaryViolet font-semibold text-[13px]">{label}:</span>
            <select
                className={`text-[13px] border rounded px-1 h-[28px] bg-white ${value === '' ? 'border-[#ccc]' : 'border-primaryViolet font-semibold'}`}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            >
                <option value="">Svi</option>
                <option value="true">Da</option>
                <option value="false">Ne</option>
            </select>
        </label>
    );
}

// markdown skraćen na nekoliko redova sa "više / manje"
function CollapsibleMarkdown({ source }: { source?: string }) {
    const [open, setOpen] = useState(false);
    const text = source ?? '';
    const isLong = text.length > 160;
    return (
        <div>
            <div className={!open && isLong ? 'max-h-[60px] overflow-hidden' : ''}>
                <MDEditor.Markdown {...mathPlugins} source={text} />
            </div>
            {isLong && (
                <button className="text-[12px] text-blue-600 hover:underline" onClick={() => setOpen((o) => !o)}>
                    {open ? 'manje' : 'više'}
                </button>
            )}
        </div>
    );
}

function KvizPitanjeManagement() {
    const [questions, setQuestions] = useState<any[]>([]);
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [resultsNum, setResultsNum] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [isLekcijaPitanje, setIsLekcijaPitanje] = useState<any>('');
    const [isKvizPitanje, setIsKvizPitanje] = useState<any>('');
    const [visible, setVisible] = useState<any>('');
    const [zaPraksu, setZaPraksu] = useState<any>('');
    const [zaTakmicenje, setZaTakmicenje] = useState<any>('');
    const [tags, setTags] = useState<string[]>([]);
    const [tezine, setTezine] = useState<string[]>([]);
    const [oblasti, setOblasti] = useState<any[]>([]);
    const [oblastId, setOblastId] = useState('');
    const totalQuestions = useRef(0);
    const timeout = useRef<any>(null);
    const [addKvizPitanjeModalOpen, setAddKvizPitanjeModalOpen] = useState(false);
    const [selectedKvizPitanje, setSelectedKvizPitanje] = useState<any>(null);
    const isFirstMount = useRef(true);
    const [uploadingExcel, setUploadingExcel] = useState(false);
    const [excelErrors, setExcelErrors] = useState<any[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [bulkUpdating, setBulkUpdating] = useState(false);
    const [savingFlags, setSavingFlags] = useState<string[]>([]);

    const loadQuestions = async (searchTerm = searchText) => {
        if (resultsNum === 0) setLoading(true);
        else setLoadingMore(true);

        const resp = await getKvizPitanjaSearch(
            limit,
            resultsNum,
            searchTerm,
            isLekcijaPitanje,
            isKvizPitanje,
            visible,
            tags,
            zaPraksu,
            zaTakmicenje,
            tezine,
            oblastId,
        );
        if (resp.status === 200) {
            const data = resp.data?.items ?? [];
            const total = resp.data?.total ?? 0;
            totalQuestions.current = total;

            if (resultsNum === 0) {
                setQuestions(data);
                setHasMore(total > data?.length);
            } else {
                setHasMore(total > questions.length + data?.length);
                setQuestions((prev) => [...prev, ...data]);
            }
        }

        setLoading(false);
        setLoadingMore(false);
    };

    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false;
            return;
        }
        if (resultsNum === 0) loadQuestions();
        else setResultsNum(0);
    }, [isLekcijaPitanje, isKvizPitanje, visible, tags, zaPraksu, zaTakmicenje, tezine, oblastId]);

    useEffect(() => {
        getOblasti().then((resp: any) => {
            if (resp?.status === 200) setOblasti([...resp.data].sort((a: any, b: any) => a.naziv.localeCompare(b.naziv)));
        });
    }, []);

    useEffect(() => {
        loadQuestions();
    }, [resultsNum]);

    const handleChange = (e: any) => {
        setSearchText(e.target.value);
        if (timeout.current) clearTimeout(timeout.current);
        timeout.current = setTimeout(() => {
            if (resultsNum === 0) loadQuestions(e.target.value);
            else setResultsNum(0);
        }, 700);
    };

    const handleClearSearch = () => {
        if (timeout.current) clearTimeout(timeout.current);
        setSearchText('');
        if (resultsNum === 0) loadQuestions('');
        else setResultsNum(0);
    };

    const reloadQuestions = () => {
        setSelectedIds([]);
        if (resultsNum === 0) loadQuestions();
        else setResultsNum(0);
    };

    const lastSelectedIndex = useRef<number | null>(null);

    // Shift + klik selektuje ceo opseg od poslednjeg kliknutog reda
    const toggleSelected = (id: string, index: number, shiftKey = false) => {
        if (shiftKey && lastSelectedIndex.current !== null) {
            const [from, to] = [Math.min(lastSelectedIndex.current, index), Math.max(lastSelectedIndex.current, index)];
            const rangeIds = questions.slice(from, to + 1).map((q) => q.id);
            setSelectedIds((prev) => Array.from(new Set([...prev, ...rangeIds])));
        } else {
            setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
        }
        lastSelectedIndex.current = index;
    };

    const toggleSelectAll = () => {
        setSelectedIds((prev) => (prev.length === questions.length ? [] : questions.map((q) => q.id)));
    };

    const bulkSetKategorija = async (field: 'zaPraksu' | 'zaTakmicenje', value: boolean) => {
        if (!selectedIds.length) return;
        setBulkUpdating(true);
        const payload = selectedIds.map((id) => ({ id, [field]: value }));
        const resp = await updateListeKvizPitanja(payload);
        if (resp.status === 200 || resp.status === 201) {
            notif(`Uspešno ste izmenili ${selectedIds.length} pitanja`);
            reloadQuestions();
        }
        setBulkUpdating(false);
    };

    // trenutna izmena jednog flag-a direktno iz liste (optimistično, uz vraćanje na staru vrednost ako snimanje ne uspe)
    const toggleFlag = async (id: string, field: FlagField, value: boolean) => {
        const key = `${id}:${field}`;
        setSavingFlags((prev) => [...prev, key]);
        setQuestions((prev) => prev.map((q) => (q.id === id ? { ...q, [field]: value } : q)));
        try {
            const resp = await updateListeKvizPitanja([{ id, [field]: value }]);
            if (resp.status !== 200 && resp.status !== 201) throw new Error();
        } catch {
            setQuestions((prev) => prev.map((q) => (q.id === id ? { ...q, [field]: !value } : q)));
            notif('Greška pri snimanju izmene');
        } finally {
            setSavingFlags((prev) => prev.filter((k) => k !== key));
        }
    };

    const changeTezina = async (id: string, staraTezina: any, imeTezine: string) => {
        const novaTezina = Number(TezinaPitanja[imeTezine as keyof typeof TezinaPitanja]);
        setQuestions((prev) => prev.map((q) => (q.id === id ? { ...q, tezina: novaTezina } : q)));
        try {
            const resp = await updateListeKvizPitanja([{ id, tezina: novaTezina }]);
            if (resp.status !== 200 && resp.status !== 201) throw new Error();
        } catch {
            setQuestions((prev) => prev.map((q) => (q.id === id ? { ...q, tezina: staraTezina } : q)));
            notif('Greška pri snimanju izmene');
        }
    };

    const openAddKvizPitanjeModal = (item: any) => {
        setSelectedKvizPitanje(item);
        setAddKvizPitanjeModalOpen(true);
    };
    const closeAddKvizPitanjeModal = () => {
        setSelectedKvizPitanje(null);
        setAddKvizPitanjeModalOpen(false);
    };

    const removeKvizPitanje = async (id: string) => {
        const confirmDelete = window.confirm('Da li ste sigurni da želite da obrišete ovo kviz pitanje?');
        if (!confirmDelete) return; // Ako korisnik klikne "Cancel", prekini funkciju
        const resp = await deleteKvizPitanjeById(id);
        if (resp.status === 200) {
            reloadQuestions();
            notif('Uspešno ste obrisali kviz pitanje');
        }
    };

    const [exportingExcel, setExportingExcel] = useState(false);

    const handleExcelExport = async () => {
        const brojZaExport = selectedIds.length > 0 ? selectedIds.length : totalQuestions.current;
        const confirmed = window.confirm(`Export-ovaćeš ${brojZaExport} pitanja u Excel. Nastaviti?`);
        if (!confirmed) return;

        setExportingExcel(true);
        try {
            const resp: any = await exportKvizPitanjaExcel(
                searchText,
                isLekcijaPitanje,
                isKvizPitanje,
                visible,
                tags,
                zaPraksu,
                zaTakmicenje,
                selectedIds,
                tezine,
                oblastId,
            );
            const url = window.URL.createObjectURL(new Blob([resp.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = 'kviz-pitanja.xlsx';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            notif('Greška pri export-u Excel fajla');
        } finally {
            setExportingExcel(false);
        }
    };

    const handleExcelUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploadingExcel(true);
        setExcelErrors([]);

        try {
            // prvo samo validacija/pregled - ništa se ne upisuje
            const preview = await previewImportKvizPitanjaExcel(file);

            if (!preview.data.success) {
                setExcelErrors(preview.data.errors);
                notif('Excel ima greške');
                return;
            }

            const { noviCount, azuriranjeCount } = preview.data;
            const confirmed = window.confirm(`Pronađeno: ${noviCount} novih pitanja, ${azuriranjeCount} izmena postojećih pitanja.\n\nPotvrdi uvoz?`);
            if (!confirmed) return;

            const result = await importKvizPitanjaExcel(file);

            if (!result.data.success) {
                setExcelErrors(result.data.errors);
                notif('Excel ima greške');
                return;
            }

            notif(result.data.message);
            reloadQuestions();
        } catch (error: any) {
            notif(error?.message || 'Greška pri importu Excel fajla');
        } finally {
            setUploadingExcel(false);
            e.target.value = '';
        }
    };

    return (
        <div className="flex h-full flex-col items-center overflow-hidden bg-[#ECEBF8] py-[6px]">
            <div className="w-full flex-1 min-h-0 max-w-[1600px] px-[6px]">
                <div className="w-full h-full min-h-0 bg-white flex flex-col px-[12px] py-[10px] rounded-[6px]">
                    <div className="shrink-0 flex flex-col gap-2 pb-2 border-b border-[#ddd]">
                        <div className="flex gap-3 items-center flex-wrap">
                            <p className="text-gray900 font-bold text-[20px]">Upravljanje kviz pitanjima</p>
                            <p className="text-primaryViolet font-bold text-[20px]">Pronađeno pitanja: {totalQuestions.current}</p>
                            <div className="flex-1"></div>
                            <button
                                disabled={exportingExcel}
                                onClick={handleExcelExport}
                                className={`px-4 h-[34px] flex items-center justify-center bg-white border border-black rounded-[4px] cursor-pointer disabled:opacity-50`}
                            >
                                {exportingExcel ? (
                                    <div className="flex items-center gap-2">
                                        <Loading size={20} primaryColor="#5E639B" stroke={2} />
                                        <span className="text-[14px] leading-4 text-black font-semibold">Export-ujem...</span>
                                    </div>
                                ) : (
                                    <span className="text-[14px] leading-4 text-black font-semibold">
                                        Export Excel {selectedIds.length > 0 ? `(${selectedIds.length} selektovano)` : '(trenutni filter)'}
                                    </span>
                                )}
                            </button>
                            {/* Dugme za upload Excel fajla */}
                            <label
                                className={`px-4 h-[34px] flex items-center justify-center bg-white border border-black rounded-[4px] cursor-pointer ${
                                    uploadingExcel ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                            >
                                <input type="file" accept=".xlsx,.xls" onChange={handleExcelUpload} className="hidden" disabled={uploadingExcel} />
                                {uploadingExcel ? (
                                    <div className="flex items-center gap-2">
                                        <Loading size={20} primaryColor="#5E639B" stroke={2} /> {/* tvoj spinner */}
                                        <span className="text-[14px] leading-4 text-black font-semibold">Učitavanje...</span>
                                    </div>
                                ) : (
                                    <span className="text-[14px] leading-4 text-black font-semibold">Import Excel</span>
                                )}
                            </label>
                            <button
                                className={`px-3 h-[34px] flex items-center justify-center bg-white border border-black rounded-[4px]  cursor-pointer `}
                                onClick={() => {
                                    openAddKvizPitanjeModal(null);
                                }}
                            >
                                <p className="text-[14px] leading-4 text-black font-semibold ">Dodaj novo kviz pitanje</p>
                            </button>
                        </div>
                        <div className="flex gap-3 items-center flex-wrap">
                            <div className="flex items-center gap-2 rounded-full border border-primaryViolet py-[5px] px-4 w-full max-w-[320px]">
                                <SearchMagnifyIcon width={18} height={18} color="#475467" />
                                <input
                                    type="text"
                                    placeholder="Pretraži"
                                    value={searchText}
                                    onChange={handleChange}
                                    className="flex-1 outline-none text-primaryViolet font-medium text-[14px] placeholder:text-gray600"
                                />
                                {searchText !== '' && (
                                    <button
                                        onClick={handleClearSearch}
                                        className="w-7 h-7 flex items-center justify-center rounded-full text-primaryViolet hover:bg-gray-200 text-xl leading-none font-bold"
                                        title="Obriši pretragu"
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                            <Autocomplete
                                multiple
                                id="tags-filled"
                                options={kvizPitanjeTagsLista.map((option) => option?.title)}
                                defaultValue={[kvizPitanjeTagsLista[0]?.title]}
                                freeSolo
                                renderTags={(value: readonly string[], getTagProps) =>
                                    value.map((option: string, index: number) => {
                                        const { key, ...tagProps } = getTagProps({ index });
                                        return <Chip variant="outlined" label={option} key={key} {...tagProps} style={{ marginTop: 15, marginBottom: 5 }} />;
                                    })
                                }
                                onChange={(event, newValue) => setTags(newValue)}
                                renderInput={(params) => <TextField {...params} variant="filled" label="tagovi" placeholder="Favoriti" />}
                                value={tags}
                                size="small"
                                style={{ width: '260px' }}
                            />
                            <Autocomplete
                                multiple
                                id="tezina-filter"
                                options={tezinaOptions}
                                value={tezine}
                                onChange={(event, newValue) => setTezine(newValue)}
                                disableCloseOnSelect
                                renderInput={(params) => <TextField {...params} variant="filled" label="težina" placeholder="Sve težine" />}
                                size="small"
                                style={{ width: '260px' }}
                            />
                            <Autocomplete
                                id="oblast-filter"
                                options={oblasti}
                                getOptionLabel={(option: any) => option?.naziv ?? ''}
                                isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                                value={oblasti.find((o) => o.id === oblastId) ?? null}
                                onChange={(event, newValue: any) => setOblastId(newValue?.id ?? '')}
                                noOptionsText="Nema oblasti"
                                renderInput={(params) => <TextField {...params} variant="filled" label="oblast" placeholder="Sve oblasti" />}
                                size="small"
                                style={{ width: '300px' }}
                            />
                        </div>
                        <div className="flex gap-4 items-center flex-wrap">
                            <YesNoFilter label="Lekcija" value={isLekcijaPitanje} onChange={setIsLekcijaPitanje} />
                            <YesNoFilter label="Kviz" value={isKvizPitanje} onChange={setIsKvizPitanje} />
                            <YesNoFilter label="Vidljivo" value={visible} onChange={setVisible} />
                            <YesNoFilter label="Vežbanje" value={zaPraksu} onChange={setZaPraksu} />
                            <YesNoFilter label="Takmičenje" value={zaTakmicenje} onChange={setZaTakmicenje} />
                            {(isLekcijaPitanje !== '' || isKvizPitanje !== '' || visible !== '' || zaPraksu !== '' || zaTakmicenje !== '') && (
                                <button
                                    className="text-[12px] font-semibold text-primaryViolet underline"
                                    onClick={() => {
                                        setIsLekcijaPitanje('');
                                        setIsKvizPitanje('');
                                        setVisible('');
                                        setZaPraksu('');
                                        setZaTakmicenje('');
                                    }}
                                >
                                    Poništi filtere
                                </button>
                            )}
                        </div>
                        {selectedIds.length > 0 && (
                            <div className="flex gap-3 items-center bg-[#F4F3FB] border border-primaryViolet rounded-[4px] px-4 py-2">
                                <p className="text-primaryViolet font-semibold text-[14px]">Izabrano {selectedIds.length} pitanja:</p>
                                <button
                                    disabled={bulkUpdating}
                                    className="px-3 h-[32px] flex items-center justify-center bg-white border border-black rounded-[4px] text-[13px] font-semibold disabled:opacity-50"
                                    onClick={() => bulkSetKategorija('zaPraksu', true)}
                                >
                                    Dodaj u Vežbanje
                                </button>
                                <button
                                    disabled={bulkUpdating}
                                    className="px-3 h-[32px] flex items-center justify-center bg-white border border-black rounded-[4px] text-[13px] font-semibold disabled:opacity-50"
                                    onClick={() => bulkSetKategorija('zaPraksu', false)}
                                >
                                    Ukloni iz Vežbanja
                                </button>
                                <button
                                    disabled={bulkUpdating}
                                    className="px-3 h-[32px] flex items-center justify-center bg-white border border-black rounded-[4px] text-[13px] font-semibold disabled:opacity-50"
                                    onClick={() => bulkSetKategorija('zaTakmicenje', true)}
                                >
                                    Dodaj u Takmičenje
                                </button>
                                <button
                                    disabled={bulkUpdating}
                                    className="px-3 h-[32px] flex items-center justify-center bg-white border border-black rounded-[4px] text-[13px] font-semibold disabled:opacity-50"
                                    onClick={() => bulkSetKategorija('zaTakmicenje', false)}
                                >
                                    Ukloni iz Takmičenja
                                </button>
                                {bulkUpdating && <Loading size={18} primaryColor="#5E639B" stroke={2} />}
                            </div>
                        )}
                        {excelErrors.length > 0 && (
                            <div className="p-3 bg-red-50 border border-red-300 rounded relative">
                                <button
                                    onClick={() => setExcelErrors([])}
                                    className="absolute top-2 right-2 w-10 h-10 flex items-center justify-center rounded-full text-red-700 hover:bg-red-200 text-2xl leading-none font-bold"
                                    title="Zatvori"
                                >
                                    ×
                                </button>
                                <p className="text-red-700 font-bold mb-2 pr-10">Greške u Excel fajlu:</p>

                                <div className="max-h-[160px] overflow-y-auto">
                                    {excelErrors.map((err, index) => (
                                        <div key={index} className="mb-3 p-3 bg-white border border-red-200 rounded">
                                            <p className="text-red-800 font-semibold">Red {err.row}</p>

                                            <ul className="list-disc ml-5 mt-1">
                                                {err.messages.map((msg: string, i: number) => (
                                                    <li key={i} className="text-red-700 text-sm">
                                                        {msg}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div id="scroll-inf-task" className="flex-1 min-h-0 overflow-y-auto flex flex-col">
                        {questions.length === 0 && !loading && (
                            <div className="flex flex-grow justify-center items-center">
                                <p className="text-center text-[14px] font-medium text-mudPurple">Lista je trenutno prazna.</p>
                            </div>
                        )}

                        {!loading && questions.length !== 0 && (
                            <InfiniteScroll
                                dataLength={questions.length}
                                next={() => setResultsNum(questions.length)}
                                hasMore={hasMore}
                                scrollableTarget="scroll-inf-task"
                                inverse={false}
                                loader={undefined}
                            >
                                <table className="compact-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 28 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.length > 0 && selectedIds.length === questions.length}
                                                    onChange={toggleSelectAll}
                                                />
                                            </th>
                                            <th style={{ width: 36 }}>Br.</th>
                                            <th>Naziv</th>
                                            <th>Opis</th>
                                            <th style={{ width: 100 }}>Težina</th>
                                            {flagColumns.map((f) => (
                                                <th key={f.field} style={{ width: 62, textAlign: 'center' }}>
                                                    {f.label}
                                                </th>
                                            ))}
                                            <th style={{ width: 140 }}>Tagovi</th>
                                            <th style={{ width: 90 }}>Akcije</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {questions.map((pitanje, index) => (
                                            <tr key={pitanje.id}>
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedIds.includes(pitanje.id)}
                                                        onChange={() => {}}
                                                        onClick={(e) => toggleSelected(pitanje.id, index, e.shiftKey)}
                                                    />
                                                </td>
                                                <td className="font-semibold">{index + 1}</td>
                                                <td className="align-top text-sm text-gray-800 overflow-hidden">
                                                    <MDEditor.Markdown {...mathPlugins} source={pitanje?.naziv} />
                                                    {pitanje?.lekcija?.naziv && (
                                                        <p className="text-[11px] text-gray600 mt-1">Lekcija: {pitanje.lekcija.naziv}</p>
                                                    )}
                                                </td>
                                                <td className="align-top text-sm text-gray-800 overflow-hidden">
                                                    <CollapsibleMarkdown source={pitanje?.opis} />
                                                </td>
                                                <td>
                                                    <select
                                                        className="text-[13px] border border-[#ccc] rounded px-1 h-[26px] bg-white"
                                                        value={TezinaPitanja[pitanje.tezina] ?? ''}
                                                        onChange={(e) => changeTezina(pitanje.id, pitanje.tezina, e.target.value)}
                                                    >
                                                        {tezinaOptions.map((t) => (
                                                            <option key={t} value={t}>
                                                                {t}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>
                                                {flagColumns.map((f) => (
                                                    <td key={f.field} style={{ textAlign: 'center' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!pitanje[f.field]}
                                                            disabled={savingFlags.includes(`${pitanje.id}:${f.field}`)}
                                                            onChange={(e) => toggleFlag(pitanje.id, f.field, e.target.checked)}
                                                            title={f.title}
                                                        />
                                                    </td>
                                                ))}
                                                <td className="text-sm text-gray-800">
                                                    {pitanje.tags && pitanje.tags.length > 0
                                                        ? [...pitanje.tags]
                                                              .sort((a, b) => a.localeCompare(b))
                                                              .map((tag: string, i: number) => (
                                                                  <Chip
                                                                      key={i}
                                                                      label={tag}
                                                                      size="small"
                                                                      style={{ marginRight: 4, marginBottom: 2, height: 20 }}
                                                                  />
                                                              ))
                                                        : '—'}
                                                </td>
                                                <td>
                                                    <div className="flex gap-2">
                                                        <button className="text-blue-600 hover:underline" onClick={() => openAddKvizPitanjeModal(pitanje)}>
                                                            Edit
                                                        </button>
                                                        <button className="text-red-600 hover:underline" onClick={() => removeKvizPitanje(pitanje?.id)}>
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </InfiniteScroll>
                        )}

                        {loading && (
                            <div className="flex w-full flex-grow items-center justify-center">
                                <Loading size={70} primaryColor="#5E639B" stroke={3} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <AddKvizPitanjeModal
                modalIsOpen={addKvizPitanjeModalOpen}
                closeModal={closeAddKvizPitanjeModal}
                lekcija={selectedKvizPitanje?.lekcija ?? {}}
                pitanjeId={selectedKvizPitanje?.id ?? null}
                reloadLekcije={() => reloadQuestions()}
            />
        </div>
    );
}

export default KvizPitanjeManagement;
